/**
 * @fileoverview Main chat interface component that orchestrates the chat experience.
 * Manages chat state, API interactions, animations, and UI components.
 *
 * @module components/chat/ChatInterface
 */

import { useEffect } from 'react'
import { useChatState } from '../../hooks/chat/useChatState'
import { useChatAPI } from '../../hooks/chat/useChatAPI'
import { useChatAnimation } from '../../hooks/chat/useChatAnimation'
import { useChatUI } from '../../hooks/chat/useChatUI'
import { useServiceWorker } from '../../hooks/useServiceWorker'
import { useAuth } from '../../hooks/auth/useAuth'
import { useConversationList } from '../../contexts/ConversationListContext'
import { ChatAnimation } from './ChatAnimation'
import { ChatMessages } from './ChatMessages'
import { ChatInput } from './ChatInput'
import { Turnstile } from '../ui/Turnstile'

/**
 * ChatInterface component - Main orchestrator for the chat experience.
 * Integrates chat state management, API communication, animations, and UI components.
 * Handles Turnstile verification, message sending, and error management.
 *
 * @component
 * @returns {JSX.Element} Rendered chat interface
 *
 * @description
 * Features:
 * - Animated logo transitions (centered → top when chat starts)
 * - SSE streaming for real-time responses
 * - Optional Turnstile bot protection
 * - Error handling and validation
 * - Responsive input positioning
 * - Message history with auto-scroll
 *
 * State Management:
 * - Uses custom hooks for separation of concerns
 * - useChatState: Messages and streaming state
 * - useChatAPI: API communication and SSE handling
 * - useChatAnimation: Logo and UI animations
 * - useChatUI: UI state (errors, loading, turnstile)
 *
 * @example
 * // Used in main app page
 * <ChatInterface />
 */
export const ChatInterface = () => {
  const chatState = useChatState()
  const { updateConversationHistory, getOrCreateConversationId, isAuthenticated } = useAuth()
  const { error, loading, turnstileToken, setError, setLoading, setTurnstileToken, clearError } =
    useChatUI()
  const animation = useChatAnimation()
  const { addNewConversation } = useConversationList()
  const { sendMessage } = useChatAPI(chatState, {
    onError: (err) => setError(err.message),
    onFirstResponse: (conversationId) => {
      // Only add to the sidebar if the user is authenticated
      if (isAuthenticated) {
        addNewConversation(conversationId)
      }
    },
  })
  const { isUpdateAvailable } = useServiceWorker()

  // Check if Turnstile is enabled
  const isTurnstileEnabled = import.meta.env.VITE_TURNSTILE_ENABLED === 'true'

  // Always show input
  const showChatInput = true

  // Helper function to clean up all temporary resume-related keys
  const cleanupResumeData = () => {
    // Remove all temporary resume-related keys
    const tempKeys = [
      'isResumingConversation',
      'resumedConversation',
      'resumedConversationId', // Was missing before - this was causing the issue
      'conversationHistory',
      'et_ai_resume_lock', // New resume lock key
      'et_ai_resume_conversation_id', // New resume conversation ID key
    ]

    tempKeys.forEach((key) => {
      localStorage.removeItem(key)
    })
  }

  // Check for resumed conversation on mount
  useEffect(() => {
    const isResuming = localStorage.getItem('isResumingConversation')
    const conversationHistory = localStorage.getItem('conversationHistory')
    const resumeLock = localStorage.getItem('et_ai_resume_lock')
    const resumeId = localStorage.getItem('et_ai_resume_conversation_id')

    if (isResuming === 'true' && conversationHistory) {
      try {
        const history = JSON.parse(conversationHistory)
        if (history && history.length > 0) {
          // Ensure we're using the database conversation ID
          if (resumeLock === 'true' && resumeId) {
            // Force AuthContext to use the database ID
            getOrCreateConversationId()
          }

          // Update chat state with database messages
          updateConversationHistory(history)
        }
      } catch (error) {
        console.error('[ChatInterface] Error loading resumed conversation:', error)
      } finally {
        // ALWAYS clean up ALL temporary keys, even on error
        cleanupResumeData()
      }
    }
  }, [updateConversationHistory, getOrCreateConversationId]) // Run only on mount

  // Cleanup SSE on unmount
  useEffect(
    () => () => {
      chatState.cleanupSSE()
    },
    [chatState]
  )

  const handleSendMessage = async (message) => {
    // Basic validation
    if (!message.trim()) {
      setError('Please enter a message')
      return
    }

    // Check Turnstile verification if enabled and this is the first message
    if (isTurnstileEnabled && !turnstileToken && !chatState.isChatMode) {
      setError('Please complete the security verification')
      return
    }

    clearError()
    setLoading(true)

    try {
      // Send message with token only on first message, clear token after successful send
      const tokenToUse = isTurnstileEnabled && !chatState.isChatMode ? turnstileToken : undefined
      await sendMessage(message, tokenToUse)

      // Clear the token after successful first message to prevent reuse
      if (tokenToUse) {
        setTurnstileToken(null)
      }
    } catch (error) {
      // If error is related to Turnstile token validation, reset the widget
      if (
        error.message.includes('Security token has expired') ||
        error.message.includes('Security verification failed')
      ) {
        setTurnstileToken(null)
        // Force user to reverify if this was the first message
        if (!chatState.isChatMode) {
          setError('Security verification expired. Please complete verification again.')
          return
        }
      }
      // Re-throw other errors
      throw error
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className={`flex flex-col relative bg-dark-primary ${
        chatState.isChatMode ? '' : 'items-center justify-center'
      }`}
      style={{ height: '100dvh' }}
    >
      <ChatAnimation
        logoVisible={animation.logoVisible}
        logoAnimated={animation.logoAnimated}
        isChatMode={chatState.isChatMode}
      />

      {chatState.isChatMode && (
        <ChatMessages
          messages={chatState.messages}
          isStreaming={chatState.isStreaming}
          loading={loading}
          updateCounter={chatState.updateCounter}
          assistantAnimationState={chatState.assistantAnimationState}
        />
      )}

      <div
        className={`${
          chatState.isChatMode
            ? 'fixed bottom-0 left-0 right-0 glassmorphism-footer'
            : 'flex-shrink-0 bg-dark-primary'
        } w-full px-8 lg:px-16 xl:px-24 pb-8 pt-4 transition-all duration-1000`}
      >
        <div className="w-full max-w-6xl mx-auto">
          <ChatInput
            onSendMessage={handleSendMessage}
            loading={loading}
            isChatMode={chatState.isChatMode}
            visible={showChatInput}
            animated={animation.inputAnimated}
            error={error}
            disabled={
              isUpdateAvailable || (isTurnstileEnabled && !turnstileToken && !chatState.isChatMode)
            }
            onFocus={() => {
              // This will trigger showing the Turnstile if needed
              if (isTurnstileEnabled && !turnstileToken && !chatState.isChatMode) {
                setError('Please complete the security verification below')
              }
            }}
            isUpdateAvailable={isUpdateAvailable}
          />

          {isTurnstileEnabled && !turnstileToken && !chatState.isChatMode && (
            <div
              className={`mt-6 flex flex-col items-center transition-opacity duration-700 ${
                animation.inputAnimated ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <p className="text-gray-400 mb-6 text-center">
                Please complete the security verification to start chatting
              </p>
              <Turnstile
                onSuccess={(token) => {
                  setTurnstileToken(token)
                  clearError()
                }}
                onError={(err) => {
                  setError(err?.userMessage || 'Security verification failed. Please try again.')
                  setTurnstileToken(null)
                }}
                onExpire={() => {
                  setTurnstileToken(null)
                  setError('Security verification expired. Please complete it again.')
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatInterface
